import React, { useState, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useDashboard } from '../../context/DashboardContext';
import { NotificationProvider } from '../../context/NotificationContext';
import SidebarNavigation from './SidebarNavigation';
import DashboardHeader from './DashboardHeader';
import DashboardOverview from './DashboardOverview';
import DashboardWidgets from './DashboardWidgets';
import { moduleRegistry } from '../../config/moduleRegistry';
import { Loader2 } from 'lucide-react';

// Loading fallback for lazy-loaded modules
const ModuleLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="w-8 h-8 text-primary animate-spin" />
      <p className="text-white/60 font-body text-sm">Loading module...</p>
    </div>
  </div>
);

// 404 for module not found
const ModuleNotFound = () => (
  <div className="flex items-center justify-center h-64">
    <div className="text-center">
      <h2 className="text-xl font-heading font-bold text-white mb-2">Module Not Found</h2>
      <p className="text-white/60 font-body text-sm">This module doesn't exist or you don't have access.</p>
    </div>
  </div>
);

// Access denied component
const AccessDenied = ({ moduleName }) => (
  <div className="flex items-center justify-center h-64">
    <div className="text-center">
      <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
        <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H9m3-5V7a3 3 0 00-6 0v4a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-2z" />
        </svg>
      </div>
      <h2 className="text-xl font-heading font-bold text-white mb-2">Access Denied</h2>
      <p className="text-white/60 font-body text-sm">
        You don't have permission to access {moduleName || 'this module'}.
      </p>
    </div>
  </div>
);

// Module route wrapper with access control
const ModuleRoute = ({ moduleId, children }) => {
  const { hasModuleAccess } = useDashboard();
  const module = moduleRegistry[moduleId];
  
  if (!module) {
    return <ModuleNotFound />;
  }
  
  if (!hasModuleAccess(moduleId)) {
    return <AccessDenied moduleName={module.name} />;
  }
  
  return children;
};

const DashboardContent = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { enabledModules, loading, dashboardConfig } = useDashboard();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 text-primary animate-spin" />
          <p className="text-white/60 font-body">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar Navigation */}
      <SidebarNavigation 
        isOpen={sidebarOpen} 
        setIsOpen={setSidebarOpen}
      />
      
      {/* Main Content Area */}
      <div className="lg:ml-64 transition-all duration-300">
        {/* Dashboard Header */}
        <DashboardHeader 
          onMenuClick={() => setSidebarOpen(true)}
        />
        
        {/* Page Content */}
        <main className="p-4 lg:p-8">
          <AnimatePresence mode="wait">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <Suspense fallback={<ModuleLoader />}>
                <Routes>
                  {/* Dashboard Overview (default) */}
                  <Route 
                    index 
                    element={
                      <DashboardOverview 
                        title={dashboardConfig?.title || 'Dashboard'}
                      />
                    } 
                  />
                  
                  {/* Dynamically render routes for enabled modules */}
                  {enabledModules.map(module => {
                    if (!module.component || module.id === 'overview') return null;
                    
                    const routePath = module.route.replace('/dashboard/', '').replace('/dashboard', '');
                    const Component = module.component;
                    
                    return (
                      <Route
                        key={module.id}
                        path={`${routePath}/*`}
                        element={
                          <ModuleRoute moduleId={module.id}>
                            <Component />
                          </ModuleRoute>
                        }
                      />
                    );
                  })}
                  
                  {/* Catch-all for unknown routes */}
                  <Route path="*" element={<ModuleNotFound />} />
                </Routes>
              </Suspense>
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
};

// Wrap with providers
const DashboardShell = () => (
  <NotificationProvider>
    <DashboardContent />
  </NotificationProvider>
);

export default DashboardShell;
